import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/common/Button';
import { Card } from '../../../components/common/Card';
import { Input } from '../../../components/common/Input';
import { Label } from '../../../components/common/Label';
import Select from '../../../components/common/Select';
import { Textarea } from '../../../components/common/Textarea';
import { FiUser, FiTruck, FiMapPin, FiDollarSign, FiPlus, FiTrash2 } from 'react-icons/fi';
import {
  DeliveryBoy,
  DeliveryBoyFormData,
  DeliveryBoyFormErrors,
  DeliveryBoyStatus,
  VehicleType,
  ShiftType,
  DeliveryArea,
  generateEmployeeId,
  DeliveryBoyAddress,
  DeliveryBoyDocument
} from '../types';

interface DeliveryBoyFormProps {
  deliveryBoy?: DeliveryBoy;
  onSubmit: (data: DeliveryBoyFormData) => Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
}

const DeliveryBoyForm: React.FC<DeliveryBoyFormProps> = ({
  deliveryBoy,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState<DeliveryBoyFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: 'male',
    status: DeliveryBoyStatus.ACTIVE,
    joiningDate: new Date().toISOString().split('T')[0],
    vehicleType: VehicleType.BIKE,
    shiftType: ShiftType.FULL_TIME,
    deliveryArea: DeliveryArea.ALL_AREAS,
    isAvailable: true,
    address: [{
      id: 1,
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
      isDefault: true
    }],
    documents: [],
    salaryType: 'fixed',
    baseSalary: 0,
    commissionRate: 0
  });

  const [errors, setErrors] = useState<DeliveryBoyFormErrors>({});
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (deliveryBoy) {
      setFormData({
        ...deliveryBoy,
        dateOfBirth: deliveryBoy.dateOfBirth ? deliveryBoy.dateOfBirth.split('T')[0] : '',
        joiningDate: deliveryBoy.joiningDate ? deliveryBoy.joiningDate.split('T')[0] : ''
      });
    }
  }, [deliveryBoy]);

  const validateForm = (): boolean => {
    const newErrors: DeliveryBoyFormErrors = {};

    // Basic validation
    if (!formData.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email format is invalid';
    }

    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone number must be 10 digits';
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    }

    // Address validation
    const defaultAddress = formData.address?.[0];
    if (!defaultAddress?.street?.trim() || !defaultAddress?.city?.trim() || 
        !defaultAddress?.state?.trim() || !defaultAddress?.pincode?.trim()) {
      newErrors.address = 'Complete address is required';
    }

    // Salary validation
    if (formData.salaryType === 'fixed' && (!formData.baseSalary || formData.baseSalary <= 0)) {
      newErrors.baseSalary = 'Base salary is required for fixed salary type';
    }

    if ((formData.salaryType === 'commission' || formData.salaryType === 'hybrid') && 
        (!formData.commissionRate || formData.commissionRate <= 0)) {
      newErrors.commissionRate = 'Commission rate is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof DeliveryBoyFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof DeliveryBoyFormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAddressChange = (index: number, field: keyof DeliveryBoyAddress, value: string) => {
    setFormData(prev => ({
      ...prev,
      address: prev.address?.map((addr, i) => 
        i === index ? { ...addr, [field]: value } : addr
      ) || []
    }));
  };

  const addDocument = () => {
    const newDocument: DeliveryBoyDocument = {
      id: Date.now(),
      type: 'aadhar',
      documentNumber: '',
      isVerified: false,
      uploadedAt: new Date().toISOString()
    };
    
    setFormData(prev => ({
      ...prev,
      documents: [...(prev.documents || []), newDocument]
    }));
  };

  const removeDocument = (index: number) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents?.filter((_, i) => i !== index) || []
    }));
  };

  const handleDocumentChange = (index: number, field: keyof DeliveryBoyDocument, value: any) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents?.map((doc, i) => 
        i === index ? { ...doc, [field]: value } : doc
      ) || []
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      alert('Please fix the errors below');
      return;
    }

    try {
      const submitData = {
        ...formData,
        employeeId: formData.employeeId || generateEmployeeId()
      };

      await onSubmit(submitData);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to save delivery boy. Please try again.');
    }
  };

  return (
    <div>
      {showSuccess && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
          Delivery boy {deliveryBoy ? 'updated' : 'created'} successfully!
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
      <Card className="p-6">
        <div className="flex items-center mb-4">
          <FiUser className="mr-2 text-blue-600" />
          <h3 className="text-lg font-semibold">Personal Information</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="Enter first name"
              />
              {errors.firstName && <p className="text-sm text-red-600 mt-1">{errors.firstName}</p>}
            </div>

            <div>
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Enter last name"
              />
              {errors.lastName && <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>}
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Enter email address"
              />
              {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
            </div>

            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="Enter phone number"
              />
              {errors.phone && <p className="text-sm text-red-600 mt-1">{errors.phone}</p>}
            </div>

            <div>
              <Label htmlFor="dateOfBirth">Date of Birth *</Label>
              <Input
                id="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
              />
              {errors.dateOfBirth && <p className="text-sm text-red-600 mt-1">{errors.dateOfBirth}</p>}
            </div>          <div>
            <Label htmlFor="gender">Gender</Label>
            <Select
              value={formData.gender}
              onChange={(value) => handleInputChange('gender', value)}
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </Select>
          </div>

          <div>
            <Label htmlFor="joiningDate">Joining Date</Label>
            <Input
              id="joiningDate"
              type="date"
              value={formData.joiningDate}
              onChange={(e) => handleInputChange('joiningDate', e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="status">Status</Label>
            <Select
              value={formData.status}
              onChange={(value) => handleInputChange('status', value)}
            >
              <option value={DeliveryBoyStatus.ACTIVE}>Active</option>
              <option value={DeliveryBoyStatus.INACTIVE}>Inactive</option>
              <option value={DeliveryBoyStatus.ON_DUTY}>On Duty</option>
              <option value={DeliveryBoyStatus.OFF_DUTY}>Off Duty</option>
              <option value={DeliveryBoyStatus.SUSPENDED}>Suspended</option>
            </Select>
          </div>
        </div>
      </Card>

      {/* Work Information */}
      <Card className="p-6">
        <div className="flex items-center mb-4">
          <FiTruck className="mr-2 text-green-600" />
          <h3 className="text-lg font-semibold">Work Information</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="vehicleType">Vehicle Type</Label>
            <Select
              value={formData.vehicleType}
              onChange={(value) => handleInputChange('vehicleType', value)}
            >
              <option value={VehicleType.BIKE}>Bike</option>
              <option value={VehicleType.SCOOTER}>Scooter</option>
              <option value={VehicleType.BICYCLE}>Bicycle</option>
              <option value={VehicleType.CAR}>Car</option>
              <option value={VehicleType.WALKING}>Walking</option>
            </Select>
          </div>

          <div>
            <Label htmlFor="vehicleNumber">Vehicle Number</Label>
            <Input
              id="vehicleNumber"
              value={formData.vehicleNumber || ''}
              onChange={(e) => handleInputChange('vehicleNumber', e.target.value)}
              placeholder="Enter vehicle number"
            />
          </div>

          <div>
            <Label htmlFor="drivingLicenseNumber">Driving License Number</Label>
            <Input
              id="drivingLicenseNumber"
              value={formData.drivingLicenseNumber || ''}
              onChange={(e) => handleInputChange('drivingLicenseNumber', e.target.value)}
              placeholder="Enter driving license number"
            />
          </div>

          <div>
            <Label htmlFor="shiftType">Shift Type</Label>
            <Select
              value={formData.shiftType}
              onChange={(value) => handleInputChange('shiftType', value)}
            >
              <option value={ShiftType.MORNING}>Morning</option>
              <option value={ShiftType.EVENING}>Evening</option>
              <option value={ShiftType.NIGHT}>Night</option>
              <option value={ShiftType.FULL_TIME}>Full Time</option>
              <option value={ShiftType.PART_TIME}>Part Time</option>
            </Select>
          </div>

          <div>
            <Label htmlFor="deliveryArea">Delivery Area</Label>
            <Select
              value={formData.deliveryArea}
              onChange={(value) => handleInputChange('deliveryArea', value)}
            >
              <option value={DeliveryArea.NORTH}>North</option>
              <option value={DeliveryArea.SOUTH}>South</option>
              <option value={DeliveryArea.EAST}>East</option>
              <option value={DeliveryArea.WEST}>West</option>
              <option value={DeliveryArea.CENTRAL}>Central</option>
              <option value={DeliveryArea.ALL_AREAS}>All Areas</option>
            </Select>
          </div>

          <div className="flex items-center">
            <input
              id="isAvailable"
              type="checkbox"
              checked={formData.isAvailable}
              onChange={(e) => handleInputChange('isAvailable', e.target.checked)}
              className="mr-2"
            />
            <Label htmlFor="isAvailable">Currently Available</Label>
          </div>
        </div>
      </Card>

      {/* Address Information */}
      <Card className="p-6">
        <div className="flex items-center mb-4">
          <FiMapPin className="mr-2 text-red-600" />
          <h3 className="text-lg font-semibold">Address Information</h3>
        </div>
        
        {formData.address?.map((addr, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label htmlFor={`street-${index}`}>Street Address *</Label>
              <Input
                id={`street-${index}`}
                value={addr.street}
                onChange={(e) => handleAddressChange(index, 'street', e.target.value)}
                placeholder="Enter street address"
              />
            </div>

            <div>
              <Label htmlFor={`city-${index}`}>City *</Label>
              <Input
                id={`city-${index}`}
                value={addr.city}
                onChange={(e) => handleAddressChange(index, 'city', e.target.value)}
                placeholder="Enter city"
              />
            </div>

            <div>
              <Label htmlFor={`state-${index}`}>State *</Label>
              <Input
                id={`state-${index}`}
                value={addr.state}
                onChange={(e) => handleAddressChange(index, 'state', e.target.value)}
                placeholder="Enter state"
              />
            </div>

            <div>
              <Label htmlFor={`pincode-${index}`}>Pincode *</Label>
              <Input
                id={`pincode-${index}`}
                value={addr.pincode}
                onChange={(e) => handleAddressChange(index, 'pincode', e.target.value)}
                placeholder="Enter pincode"
              />
            </div>
          </div>
        ))}
        {errors.address && <p className="text-sm text-red-600 mt-1">{errors.address}</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <Label htmlFor="emergencyContactName">Emergency Contact Name</Label>
            <Input
              id="emergencyContactName"
              value={formData.emergencyContactName || ''}
              onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
              placeholder="Enter emergency contact name"
            />
          </div>

          <div>
            <Label htmlFor="emergencyContactPhone">Emergency Contact Phone</Label>
            <Input
              id="emergencyContactPhone"
              value={formData.emergencyContactPhone || ''}
              onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
              placeholder="Enter emergency contact phone"
            />
          </div>
        </div>
      </Card>

      {/* Financial Information */}
      <Card className="p-6">
        <div className="flex items-center mb-4">
          <FiDollarSign className="mr-2 text-green-600" />
          <h3 className="text-lg font-semibold">Financial Information</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="salaryType">Salary Type</Label>
            <Select
              value={formData.salaryType}
              onChange={(value) => handleInputChange('salaryType', value)}
            >
              <option value="fixed">Fixed Salary</option>
              <option value="commission">Commission Based</option>
              <option value="hybrid">Fixed + Commission</option>
            </Select>
          </div>

          {(formData.salaryType === 'fixed' || formData.salaryType === 'hybrid') && (
            <div>
              <Label htmlFor="baseSalary">Base Salary (₹)</Label>
              <Input
                id="baseSalary"
                type="number"
                value={formData.baseSalary || ''}
                onChange={(e) => handleInputChange('baseSalary', parseFloat(e.target.value) || 0)}
                placeholder="Enter base salary"
              />
              {errors.baseSalary && <p className="text-sm text-red-600 mt-1">{errors.baseSalary}</p>}
            </div>
          )}

          {(formData.salaryType === 'commission' || formData.salaryType === 'hybrid') && (
            <div>
              <Label htmlFor="commissionRate">Commission Rate (%)</Label>
              <Input
                id="commissionRate"
                type="number"
                value={formData.commissionRate || ''}
                onChange={(e) => handleInputChange('commissionRate', parseFloat(e.target.value) || 0)}
                placeholder="Enter commission rate"
              />
              {errors.commissionRate && <p className="text-sm text-red-600 mt-1">{errors.commissionRate}</p>}
            </div>
          )}

          <div>
            <Label htmlFor="accountNumber">Account Number</Label>
            <Input
              id="accountNumber"
              value={formData.accountNumber || ''}
              onChange={(e) => handleInputChange('accountNumber', e.target.value)}
              placeholder="Enter bank account number"
            />
          </div>

          <div>
            <Label htmlFor="ifscCode">IFSC Code</Label>
            <Input
              id="ifscCode"
              value={formData.ifscCode || ''}
              onChange={(e) => handleInputChange('ifscCode', e.target.value)}
              placeholder="Enter IFSC code"
            />
          </div>

          <div>
            <Label htmlFor="bankName">Bank Name</Label>
            <Input
              id="bankName"
              value={formData.bankName || ''}
              onChange={(e) => handleInputChange('bankName', e.target.value)}
              placeholder="Enter bank name"
            />
          </div>
        </div>
      </Card>

      {/* Documents Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Documents</h3>
          <Button
            type="button"
            onClick={addDocument}
            variant="outline"
            size="sm"
          >
            <FiPlus className="w-4 h-4 mr-2" />
            Add Document
          </Button>
        </div>
        
        {formData.documents?.map((doc, index) => (
          <div key={doc.id} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 border rounded-lg">
            <div>
              <Label htmlFor={`docType-${index}`}>Document Type</Label>
              <Select
                value={doc.type}
                onChange={(value) => handleDocumentChange(index, 'type', value)}
              >
                <option value="aadhar">Aadhar Card</option>
                <option value="pan">PAN Card</option>
                <option value="driving_license">Driving License</option>
                <option value="voter_id">Voter ID</option>
                <option value="passport">Passport</option>
              </Select>
            </div>

            <div>
              <Label htmlFor={`docNumber-${index}`}>Document Number</Label>
              <Input
                id={`docNumber-${index}`}
                value={doc.documentNumber}
                onChange={(e) => handleDocumentChange(index, 'documentNumber', e.target.value)}
                placeholder="Enter document number"
              />
            </div>

            <div className="flex items-end">
              <Button
                type="button"
                onClick={() => removeDocument(index)}
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-800"
              >
                <FiTrash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </Card>

      {/* Notes */}
      <Card className="p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Additional Notes</h3>
        </div>
        
        <div>
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            value={formData.notes || ''}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Enter any additional notes..."
            rows={4}
          />
        </div>
      </Card>

      {/* Form Actions */}
      <div className="flex justify-end space-x-4">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={loading}
        >
          {loading ? 'Saving...' : deliveryBoy ? 'Update Delivery Boy' : 'Create Delivery Boy'}
        </Button>
      </div>
    </form>
  </div>
  );
};

export default DeliveryBoyForm;